<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';

const emit = defineEmits(['select']);

const { t } = useI18n();
const store = useStore();
const dialogRef = ref(null);
const searchQuery = ref('');

const currentUser = useMapGetter('getCurrentUser');
const agents = useMapGetter('agents/getAgents');
const isFetching = useMapGetter('agents/getUIFlags');

const filteredAgents = computed(() => {
  const query = searchQuery.value.toLowerCase().trim();
  return (agents.value || [])
    .filter(agent => agent.id !== currentUser.value?.id && agent.confirmed)
    .filter(agent => {
      if (!query) return true;
      const name = (agent.name || '').toLowerCase();
      const email = (agent.email || '').toLowerCase();
      return name.includes(query) || email.includes(query);
    });
});

onMounted(() => {
  if (!agents.value?.length) {
    store.dispatch('agents/get');
  }
});

const open = () => {
  searchQuery.value = '';
  if (!agents.value?.length) {
    store.dispatch('agents/get');
  }
  dialogRef.value?.open();
};

const close = () => {
  dialogRef.value?.close();
};

const selectAgent = agent => {
  emit('select', agent);
  close();
};

defineExpose({ open, close });
</script>

<template>
  <Dialog
    ref="dialogRef"
    type="edit"
    width="md"
    :title="t('INTERNAL_CHAT.NEW_DM_TITLE')"
    :description="t('INTERNAL_CHAT.NEW_DM_DESCRIPTION')"
    :show-confirm-button="false"
    :cancel-button-label="t('INTERNAL_CHAT.CANCEL')"
    overflow-y-auto
  >
    <div class="flex flex-col gap-3">
      <input
        v-model="searchQuery"
        type="text"
        class="w-full rounded-lg border border-n-weak px-3 py-2 bg-n-background text-sm"
        :placeholder="t('INTERNAL_CHAT.SEARCH_AGENTS')"
      />
      <div
        v-if="isFetching.isFetching"
        class="text-sm text-n-slate-11 py-4 text-center"
      >
        {{ t('INTERNAL_CHAT.LOADING') }}
      </div>
      <div
        v-else-if="!filteredAgents.length"
        class="text-sm text-n-slate-11 py-4 text-center"
      >
        {{ t('INTERNAL_CHAT.NO_AGENTS') }}
      </div>
      <ul v-else class="flex flex-col gap-1 max-h-72 overflow-y-auto">
        <li v-for="agent in filteredAgents" :key="agent.id">
          <button
            type="button"
            class="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-n-alpha-2 text-left"
            @click="selectAgent(agent)"
          >
            <span
              class="flex items-center justify-center w-8 h-8 rounded-full bg-n-brand text-white text-xs font-medium shrink-0"
            >
              {{ (agent.name || agent.email || '?').charAt(0).toUpperCase() }}
            </span>
            <span class="min-w-0">
              <span class="block text-sm font-medium truncate">{{
                agent.name
              }}</span>
              <span class="block text-xs text-n-slate-11 truncate">{{
                agent.email
              }}</span>
            </span>
          </button>
        </li>
      </ul>
    </div>
  </Dialog>
</template>
